import type {
	GridColumnProps,
	GridCustomCellProps,
} from "@tql/ui-components-react/kendo/grid";

import { ActionCell } from "~/shared/components/trax-grid/action-cell/action-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import { MaskedTextboxCell } from "~/shared/components/trax-grid/masked-textbox-cell/masked-textbox-cell.tsx";
import { SelectCell } from "~/shared/components/trax-grid/select-cell/select-cell.tsx";
import { TextboxCell } from "~/shared/components/trax-grid/textbox-cell/textbox-cell.tsx";
import { TimeRangeCell } from "~/shared/components/trax-grid/time-range-cell/time-range-cell.tsx";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types";
import { formatPhoneNumber } from "~/shared/utils/string-fns.ts";

import type { EditDescriptor } from "../../hooks/use-address-editing";
import type { SelectDescriptor } from "../../hooks/use-address-selection";
import type { CityStateZipFields } from "../city-state-zip-cell/city-state-zip-cell.tsx";
import { CityStateZipCell } from "../city-state-zip-cell/city-state-zip-cell.tsx";

type ColumnConfigParams = {
	type: SubGroupKey;
	isEditMode: boolean;
	isLoading: boolean;
	edit: EditDescriptor;
	select: SelectDescriptor;
	handleSelect: (id: string | number) => void;
	handleDeselect: (id: string | number) => void;
	handleRemoveFromList: (dataItem: AddressBookItemSummary) => void;
	handleAddAndClose: (dataItem: AddressBookItemSummary) => void;
	enterEdit: (dataItem: AddressBookItemSummary) => void;
	applyAddressFields: (
		dataItem: AddressBookItemSummary,
		fields: CityStateZipFields,
	) => void;
};

export const getAddressBookColumnConfig = ({
	type,
	isEditMode,
	isLoading,
	edit,
	select,
	handleSelect,
	handleDeselect,
	handleRemoveFromList,
	handleAddAndClose,
	enterEdit,
	applyAddressFields,
}: ColumnConfigParams): GridColumnProps[] => {
	return [
		{
			title: "",
			width: 30,
			filterable: false,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<SelectCell
							{...props}
							onDeselect={handleDeselect}
							onSelect={handleSelect}
							select={select}
						/>
					),
			},
		},
		{
			field: "companyName",
			title: `${type == "pickup" ? "Shipper" : "Receiver"} Name`,
			width: 175,
			editor: "text",
			filterable: true,
		},
		{
			field: "address1",
			title: "Address",
			width: 175,
			editor: "text",
			filterable: true,
		},
		{
			field: "address2",
			title: "Address 2",
			width: 175,
			editor: "text",
			filterable: true,
		},
		{
			field: "cityStateZip",
			title: "City, State, Zip",
			width: 175,
			editable: false,
			filterable: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<CityStateZipCell
							{...props}
							edit={edit}
							onApply={applyAddressFields}
						/>
					),
			},
		},
		{
			field: "contactName",
			title: "Contact",
			width: 100,
			editor: "text",
			filterable: true,
		},
		{
			field: "contactPhone",
			title: "Phone",
			width: 150,
			editor: "text",
			cells: {
				data: (props: GridCustomCellProps) => {
					if (isLoading) return <LoaderCell {...props} />;
					const item = props.dataItem as AddressBookItemSummary;
					const isInEdit = edit[item.addressBookItemId] === true;
					return (
						<MaskedTextboxCell
							{...props}
							formatValue={formatPhoneNumber}
							isInEdit={isInEdit}
							mask={"(000) 000-0000"}
							placeholder={"(___) ___-____"}
						/>
					);
				},
			},
			filterable: true,
		},
		{
			field: "contactExtension",
			title: "Ext",
			width: 75,
			editor: "text",
			filterable: true,
			cells: {
				data: (props: GridCustomCellProps) => {
					if (isLoading) return <LoaderCell {...props} />;
					const item = props.dataItem as AddressBookItemSummary;
					const isInEdit = edit[item.addressBookItemId] === true;
					return (
						<TextboxCell
							{...props}
							inputMode={"numeric"}
							isInEdit={isInEdit}
							maxLength={10}
						/>
					);
				},
			},
		},

		{
			field: "hoursOpen",
			title: `${type == "pickup" ? "Shipper" : "Receiver"} Hours`,
			width: isEditMode ? 200 : 150,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<TimeRangeCell {...props} edit={edit} />
					),
			},
			filterable: true,
		},
		{
			width: isEditMode ? 100 : 50,
			filterable: false,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<ActionCell<AddressBookItemSummary>
							{...props}
							onAddAndClose={handleAddAndClose}
							onEdit={enterEdit}
							onRemoveFromList={handleRemoveFromList}
						/>
					),
				edit: {
					text: (props: GridCustomCellProps) => (
						<GridTd tdProps={props.tdProps}></GridTd>
					),
				},
			},
		},
	];
};
